import React, { useState, useEffect } from 'react';
import axios, { API } from '../../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Spinner, PaperPlaneTilt, EnvelopeSimple, Check, Warning
} from '@phosphor-icons/react';

/**
 * Reusable "Send by Email" modal.
 * Props:
 *   reportType: "prep_report" | "doctor_brief" | "health_summary" | "document_summary"
 *   reportId: appointment_id or document_id
 *   defaultSubject: prefilled subject line
 *   onClose: () => void
 */
const SendEmailModal = ({ reportType, reportId, defaultSubject, onClose }) => {
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(defaultSubject || '');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // 'sent' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [configured, setConfigured] = useState(true);

  useEffect(() => {
    axios.get(`${API}/email/status`).then(({ data }) => {
      setConfigured(data.configured);
    }).catch(() => setConfigured(false));
  }, []);

  const handleSend = async () => {
    if (!email.trim()) return;
    setSending(true);
    setResult(null);
    try {
      await axios.post(`${API}/email/send-report`, {
        recipient_email: email.trim(),
        report_type: reportType,
        report_id: reportId,
        subject: subject || undefined,
        message: message || undefined,
      });
      setResult('sent');
    } catch (err) {
      setResult('error');
      setErrorMsg(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center sm:justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl p-6"
        data-testid="send-email-modal"
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <EnvelopeSimple size={20} className="text-[#235347]" />
            <h2 className="text-lg font-medium text-slate-900">Send Report</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={24} />
          </button>
        </div>

        {!configured ? (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
            <div className="flex items-start gap-2">
              <Warning size={18} className="text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Email not configured</p>
                <p className="text-xs text-amber-600 mt-1">SMTP settings need to be configured to send emails. Add SMTP_HOST, SMTP_USER, SMTP_PASS, and SMTP_FROM to your environment.</p>
              </div>
            </div>
          </div>
        ) : result === 'sent' ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-3">
              <Check size={28} className="text-green-600" />
            </div>
            <p className="font-medium text-slate-900">Report sent</p>
            <p className="text-sm text-slate-500 mt-1">Delivered to {email}</p>
            <button
              onClick={onClose}
              className="mt-4 px-6 py-2 bg-[#235347] text-white rounded-full text-sm font-medium"
              data-testid="email-done-btn"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Recipient email *</label>
                <input
                  type="email"
                  placeholder="doctor@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#235347]/20"
                  data-testid="email-recipient-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
                  data-testid="email-subject-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Personal message (optional)</label>
                <textarea
                  placeholder="Add a note for the recipient..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none min-h-[60px] resize-none"
                  data-testid="email-message-input"
                />
              </div>
            </div>

            {result === 'error' && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{errorMsg}</p>
              </div>
            )}

            <button
              onClick={handleSend}
              disabled={!email.trim() || sending}
              className="w-full mt-5 bg-[#235347] text-white rounded-full px-6 py-3 font-medium hover:bg-[#1A3F36] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="send-email-btn"
            >
              {sending ? (
                <><Spinner size={16} className="animate-spin" /> Sending...</>
              ) : (
                <><PaperPlaneTilt size={16} /> Send Report</>
              )}
            </button>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

export default SendEmailModal;
